import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import moment from 'moment';
import intl from '../intl';

const defaultInitialState = {
	value: {},
	tmpValue: {},
	isOpen: false,
	isRightPosition: true,
	userTimeZoneIsSet: false,
	ranges: {
		'Today': {
			startDate: e => e,
			endDate: e => e
		},
		'Today and yesterday': {
			startDate: e => e.add(-1, 'days'),
			endDate: e => e
		},
		'Yesterday': {
			startDate: e => e.add(-1, 'days'),
			endDate: e => e.add(-1, 'days')
		},
		'This week': {
			startDate: e => e.startOf('isoWeek'),
			endDate: e => e.endOf('day')
		},
		'Last week': {
			startDate: e => e.subtract(7, 'day').startOf('isoWeek'),
			endDate: e => e.subtract(7, 'day').endOf('isoWeek')
		},
		'Current month': {
			startDate: e => e.startOf('month'),
			endDate: e => e.endOf('day')
		},
		'Last month': {
			startDate: e => e.subtract(1, 'month').startOf('month'),
			endDate: e => e.subtract(1, 'month').endOf('month')
		},
		'This year': {
			startDate: e => e.startOf('year'),
			endDate: e => e.endOf('day')
		}
	}
};

const createCalendar = ({
	namespace,
	stateNodeName,
	defaultValue
}) => {
	// action types
	const TOGGLE = `${namespace}/${stateNodeName}-toggle`;
	const CHANGE = `${namespace}/${stateNodeName}-change`;
	const APPLY = `${namespace}/${stateNodeName}-apply`;

	const SET_USER_TIMEZONE_DATE = `${namespace}/${stateNodeName}-set-user-timezone-date`;

	// init state
	const initialState = {
		...defaultInitialState,
		value: defaultValue
	};

	// actions
	const actions = {
		[`${stateNodeName}Init`]: () => dispatch => {
			const action = actions[`${stateNodeName}SetUserTimezoneDate`];
			dispatch(action());
		},
		[`${stateNodeName}Toggle`]: () => ({ type: TOGGLE }),
		[`${stateNodeName}Change`]: date => ({
			type: CHANGE,
			payload: date
		}),
		[`${stateNodeName}Apply`]: date => ({
			type: APPLY,
			payload: date
		}),
		[`${stateNodeName}SetUserTimezoneDate`]: () => (dispatch, getState) => {
			const { app: { userData: { general: { timezone } } } } = getState();
			const { value: { start, end }, userTimeZoneIsSet } = getState()[namespace][stateNodeName];

			const convertDateToZoneDateFn = (date, timezone) => {
				const iso = moment(date).format('YYYY-MM-DD');
				const localOffset = moment().tz(moment.tz.guess()).format('THH:mm:ssZ');
				return moment(iso + localOffset)
					.tz(timezone)
					.format('YYYY-MM-DD');
			};

			if (!userTimeZoneIsSet) {
				dispatch({
					type: SET_USER_TIMEZONE_DATE,
					payload: {
						start: convertDateToZoneDateFn(start, timezone),
						end: convertDateToZoneDateFn(end, timezone)
					}
				});
			}

			return userTimeZoneIsSet;
		}
	};

	// reducers
	const reducers = {
		[TOGGLE]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				isOpen: !state[stateNodeName].isOpen
			}
		}),
		[CHANGE]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				tmpValue: {
					start: action.payload.startDate.format('YYYY-MM-DD'),
					end: action.payload.endDate.format('YYYY-MM-DD')
				}
			}
		}),
		[APPLY]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				isOpen:false,
				value: isEmpty(state[stateNodeName].tmpValue) ?
					state[stateNodeName].value : { ...state[stateNodeName].tmpValue }
			}
		}),
		[SET_USER_TIMEZONE_DATE]: (state, action) => {
			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					value: action.payload,
					userTimeZoneIsSet: true
				}
			};
		}
	};

	// selectors
	const selectors = {
		getCalendar: (calendarSelector, appState) => createSelector(
			calendarSelector,
			appState,
			(calendar, appState) => {
				const { start, end } = calendar.value;
				const dateRange = values({
					start: moment(start).format('DD.MM.YYYY'),
					end: moment(end).format('DD.MM.YYYY')
				});
				const startDate = moment(start);
				const endDate = moment(end);
				const translator = intl(appState);

				const ranges = {};

				Object.keys(calendar.ranges).forEach(key => {
					ranges[[translator(key)]] = calendar.ranges[key];
				});

				const buttons = {
					'Apply': translator('Применить'),
					'Close': translator('Закрыть')
				};

				return {
					...calendar,
					ranges,
					dateRange,
					startDate,
					endDate,
					buttons
				};
			}
		)
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

export default  createCalendar;



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/shared/calendar/index.js